import React, { useEffect, useRef, useState, useCallback } from 'react';
import { getSocket } from '../../socket';
import { FiMicOff, FiMic, FiVideo, FiPhoneCall } from 'react-icons/fi';
import { IoCall } from 'react-icons/io5';
import { FaVideo, FaVideoSlash } from 'react-icons/fa';

const formatDuration = (seconds) => {
  const mins = Math.floor(seconds / 60).toString().padStart(2, '0');
  const secs = Math.floor(seconds % 60).toString().padStart(2, '0');
  return `${mins}:${secs}`;
};

export default function WebRTCHandler({
  channel,
  isVideo,
  isCaller,
  receiverId,
  receiverName,
  receiverPic,
  credentials,
  onClose
}) {
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const remoteStreamRef = useRef(new MediaStream());
  const pcRef = useRef(null);
  const localStreamRef = useRef(null);
  const hasSentOfferRef = useRef(false);

  const [pc, setPc] = useState(null);
  const [micOn, setMicOn] = useState(true);
  const [cameraOn, setCameraOn] = useState(isVideo);
  const [accepted, setAccepted] = useState(!isCaller); // Receiver is accepted by default
  const [connected, setConnected] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [localStream, setLocalStream] = useState(null);

  const pendingOfferRef = useRef(null);
  const pendingCandidatesRef = useRef([]);

  const setRemoteVideoRef = useCallback((node) => {
    remoteVideoRef.current = node;
    if (node && remoteStreamRef.current) {
      if (node.srcObject !== remoteStreamRef.current) {
        node.srcObject = remoteStreamRef.current;
      }
      node.play?.().catch(e => {
        if (e.name !== 'AbortError') {
          console.log('[WEBRTC] Video play catch:', e);
        }
      });
    }
  }, []);

  const setRemoteAudioRef = useCallback((node) => {
    remoteAudioRef.current = node;
    if (node && remoteStreamRef.current) {
      if (node.srcObject !== remoteStreamRef.current) {
        node.srcObject = remoteStreamRef.current;
      }
      node.play?.().catch(e => {
        if (e.name !== 'AbortError') {
          console.log('[WEBRTC] Audio play catch:', e);
        }
      });
    }
  }, []);

  const parseIceCandidate = (candidateData) => {
    if (!candidateData) return null;
    try {
      if (typeof candidateData === 'string') {
        return new RTCIceCandidate({ candidate: candidateData });
      }
      const candidateStr = candidateData.candidate || (typeof candidateData === 'object' ? candidateData.candidate : '');
      const sdpMid = candidateData.sdpMid !== undefined ? candidateData.sdpMid : (candidateData.sdpMid || undefined);
      const sdpMLineIndex = candidateData.sdpMLineIndex !== undefined && candidateData.sdpMLineIndex !== null
        ? Number(candidateData.sdpMLineIndex)
        : undefined;

      return new RTCIceCandidate({
        candidate: candidateStr,
        sdpMid: sdpMid,
        sdpMLineIndex: sdpMLineIndex
      });
    } catch (e) {
      console.error('[WEBRTC] ICE candidate parse error:', e, candidateData);
      return null;
    }
  };

  // Flush ICE candidates helper
  const flushPendingCandidates = useCallback(async (peerConnection) => {
    if (pendingCandidatesRef.current.length > 0) {
      console.log(`[WEBRTC] Flushing ${pendingCandidatesRef.current.length} pending ICE candidates...`);
      for (const candidateData of pendingCandidatesRef.current) {
        try {
          const iceCandidate = parseIceCandidate(candidateData);
          if (iceCandidate) {
            await peerConnection.addIceCandidate(iceCandidate);
          }
        } catch (e) {
          console.error('Error adding buffered ICE candidate:', e);
        }
      }
      pendingCandidatesRef.current = [];
    }
  }, []);

  // Timer: Start when accepted OR connected
  useEffect(() => {
    if (!accepted && !connected) {
      setElapsedSeconds(0);
      return undefined;
    }
    const timer = setInterval(() => setElapsedSeconds(prev => prev + 1), 1000);
    return () => clearInterval(timer);
  }, [accepted, connected]);

  const attachRemoteStream = useCallback((stream) => {
    if (!stream) return;

    stream.getTracks().forEach(track => {
      track.enabled = true;
      if (!remoteStreamRef.current.getTracks().some(existing => existing.id === track.id)) {
        remoteStreamRef.current.addTrack(track);
      }
    });

    const playableStream = remoteStreamRef.current.getTracks().length > 0
      ? remoteStreamRef.current
      : stream;

    if (!isVideo && remoteAudioRef.current) {
      remoteAudioRef.current.srcObject = playableStream;
      remoteAudioRef.current.muted = false;
      remoteAudioRef.current.volume = 1;
      remoteAudioRef.current.play?.().catch(e => {
        if (e.name !== 'AbortError') {
          console.log('[WEBRTC] Audio play catch:', e);
        }
      });
    }

    if (remoteVideoRef.current) {
      remoteVideoRef.current.srcObject = playableStream;
      remoteVideoRef.current.play?.().catch(e => {
        if (e.name !== 'AbortError') {
          console.log('[WEBRTC] Video play catch:', e);
        }
      });
    }
  }, [isVideo]);

  const stopLocalMedia = useCallback(() => {
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(t => t.stop());
      localStreamRef.current = null;
    }
    if (localVideoRef.current) {
      localVideoRef.current.pause?.();
      localVideoRef.current.srcObject = null;
    }
    setLocalStream(null);
  }, []);

  const detachRemoteMedia = useCallback(() => {
    if (remoteStreamRef.current) {
      remoteStreamRef.current.getTracks().forEach(t => t.stop());
      remoteStreamRef.current = new MediaStream();
    }
    if (remoteAudioRef.current) {
      remoteAudioRef.current.pause?.();
      remoteAudioRef.current.srcObject = null;
    }
    if (remoteVideoRef.current) {
      remoteVideoRef.current.pause?.();
      remoteVideoRef.current.srcObject = null;
    }
  }, []);

  const closePeerConnection = useCallback(() => {
    if (pcRef.current) {
      pcRef.current.onicecandidate = null;
      pcRef.current.ontrack = null;
      pcRef.current.onconnectionstatechange = null;
      pcRef.current.oniceconnectionstatechange = null;
      pcRef.current.close();
      pcRef.current = null;
    }
    setPc(null);
  }, []);

  const endLocalCallSession = useCallback(() => {
    stopLocalMedia();
    detachRemoteMedia();
    closePeerConnection();
    setAccepted(false);
    setConnected(false);
    hasSentOfferRef.current = false;
    pendingOfferRef.current = null;
    pendingCandidatesRef.current = [];
  }, [stopLocalMedia, detachRemoteMedia, closePeerConnection]);

  // Initialize WebRTC
  const initWebRTC = useCallback(async () => {
    try {
      const stunUrl = credentials?.stun_url || 'stun:stun.l.google.com:19302';
      const iceServers = [{ urls: stunUrl.split(',').map(url => url.trim()).filter(Boolean) }];
      if (credentials?.turn_url) {
        const turnUrls = credentials.turn_url.split(',').map(url => url.trim()).filter(Boolean);
        const turnEntry = { urls: turnUrls.length === 1 ? turnUrls[0] : turnUrls };
        if (credentials.turn_username) turnEntry.username = credentials.turn_username;
        if (credentials.turn_password) turnEntry.credential = credentials.turn_password;
        iceServers.push(turnEntry);
        // Also add transport=tcp variant for Coturn VPS firewall compatibility
        const tcpUrls = turnUrls
          .filter(url => !url.includes('transport='))
          .map(url => url.includes('?') ? `${url}&transport=tcp` : `${url}?transport=tcp`);
        if (tcpUrls.length > 0) {
          const tcpEntry = { urls: tcpUrls.length === 1 ? tcpUrls[0] : tcpUrls };
          if (credentials.turn_username) tcpEntry.username = credentials.turn_username;
          if (credentials.turn_password) tcpEntry.credential = credentials.turn_password;
          iceServers.push(tcpEntry);
        }
      }
      iceServers.push({ urls: 'stun:stun.l.google.com:19302' });
      iceServers.push({ urls: 'stun:stun1.l.google.com:19302' });

      console.log('[WEBRTC_DEBUG] Initializing RTCPeerConnection with iceServers:', JSON.stringify(iceServers));

      const peerConnection = new RTCPeerConnection({ iceServers });
      pcRef.current = peerConnection;

      peerConnection.onicecandidate = (event) => {
        if (event.candidate) {
          console.log('[WEBRTC_DEBUG] Generated ICE Candidate:', event.candidate.candidate);
          const socket = getSocket();
          socket?.emit('webrtc_ice_candidate', {
            channel,
            targetId: receiverId,
            candidate: event.candidate
          });
        } else {
          console.log('[WEBRTC_DEBUG] ICE Candidate gathering completed');
        }
      };

      peerConnection.onconnectionstatechange = () => {
        const state = peerConnection.connectionState;
        console.log('[WEBRTC_DEBUG] Connection State Changed:', state);
        if (state === 'connected') {
          setConnected(true);
        } else if (state === 'failed' || state === 'disconnected' || state === 'closed') {
          setConnected(false);
        }
      };

      peerConnection.oniceconnectionstatechange = () => {
        const state = peerConnection.iceConnectionState;
        console.log('[WEBRTC_DEBUG] ICE Connection State Changed:', state);
        if (state === 'connected' || state === 'completed') {
          setConnected(true);
        } else if (state === 'failed') {
          console.error('[WEBRTC_DEBUG] ICE Connection Failed! Check TURN server or network firewall.');
          peerConnection.restartIce?.();
        }
      };

      peerConnection.ontrack = (event) => {
        console.log('[WEBRTC_DEBUG] Remote track received:', event.track.kind, 'id:', event.track.id, 'streams:', event.streams.length);
        setConnected(true);
        if (event.track) {
          event.track.enabled = true;
        }
        const incomingStream = event.streams?.[0] || new MediaStream([event.track]);
        attachRemoteStream(incomingStream);
        if (event.track) {
          event.track.onunmute = () => attachRemoteStream(incomingStream);
        }
      };

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: isVideo
      });

      localStreamRef.current = stream;
      setLocalStream(stream);
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }

      stream.getTracks().forEach(track => {
        peerConnection.addTrack(track, stream);
      });

      setPc(peerConnection);
    } catch (err) {
      console.error('WebRTC initialization error:', err);
    }
  }, [channel, isVideo, receiverId, credentials, attachRemoteStream]);

  useEffect(() => {
    initWebRTC();
    return () => {
      endLocalCallSession();
    };
    // eslint-disable-next-deps
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Process buffered offer when pc becomes ready
  useEffect(() => {
    if (pc && pendingOfferRef.current) {
      const processPendingOffer = async () => {
        const data = pendingOfferRef.current;
        pendingOfferRef.current = null;
        try {
          console.log('[WEBRTC] Processing buffered offer...');
          await pc.setRemoteDescription(new RTCSessionDescription(data.sdp));
          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);
          const socket = getSocket();
          socket?.emit('webrtc_answer', { channel, callerId: receiverId, sdp: answer });
          setAccepted(true);
          setConnected(true);
          await flushPendingCandidates(pc);
        } catch (e) {
          console.error("Error processing buffered WebRTC offer:", e);
        }
      };
      processPendingOffer();
    }
  }, [pc, channel, receiverId, flushPendingCandidates]);

  // Caller: Generate SDP Offer once call is accepted
  useEffect(() => {
    if (accepted && isCaller && pc && localStream && !hasSentOfferRef.current) {
      const makeOffer = async () => {
        try {
          hasSentOfferRef.current = true;
          console.log('[WEBRTC] Creating and sending SDP offer...');
          const offer = await pc.createOffer();
          await pc.setLocalDescription(offer);
          const socket = getSocket();
          socket?.emit('webrtc_offer', {
            receiverId,
            sdp: offer,
            channel
          });
        } catch (e) {
          hasSentOfferRef.current = false;
          console.error("Error creating WebRTC offer:", e);
        }
      };
      makeOffer();
    }
  }, [accepted, isCaller, pc, localStream, receiverId, channel]);

  // Signaling message handlers
  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;

    const handleOffer = async (data) => {
      if (data?.channel && channel && String(data.channel) !== String(channel)) return;
      if (!pc) {
        console.log('[WEBRTC] Offer received but PeerConnection is not ready yet. Buffering...');
        pendingOfferRef.current = data;
        return;
      }
      try {
        console.log('[WEBRTC] Offer received. Setting remote description and sending answer...');
        await pc.setRemoteDescription(new RTCSessionDescription(data.sdp));
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        socket.emit('webrtc_answer', { channel, callerId: receiverId, sdp: answer });
        setAccepted(true);
        setConnected(true);
        await flushPendingCandidates(pc);
      } catch (e) {
        console.error("Error handling WebRTC offer:", e);
      }
    };

    const handleAnswer = async (data) => {
      if (data?.channel && channel && String(data.channel) !== String(channel)) return;
      if (!pc) return;
      if (pc.signalingState !== 'have-local-offer') {
        console.log('[WEBRTC] Ignoring duplicate answer, signalingState is already:', pc.signalingState);
        return;
      }
      try {
        console.log('[WEBRTC] Answer received. Setting remote description...');
        await pc.setRemoteDescription(new RTCSessionDescription(data.sdp));
        setAccepted(true);
        setConnected(true);
        await flushPendingCandidates(pc);
      } catch (e) {
        console.error("Error handling WebRTC answer:", e);
      }
    };

    const handleCandidate = async (data) => {
      if (data?.channel && channel && String(data.channel) !== String(channel)) return;
      if (!pc || !pc.remoteDescription) {
        console.log('[WEBRTC] Candidate received but pc remote description is not set yet. Buffering candidate...');
        pendingCandidatesRef.current.push(data.candidate);
        return;
      }
      try {
        const iceCandidate = parseIceCandidate(data.candidate);
        if (iceCandidate) {
          await pc.addIceCandidate(iceCandidate);
        }
      } catch (e) {
        console.error('Error adding ICE candidate:', e);
      }
    };

    const handleCallAccepted = (data) => {
      if (!data?.channel || String(data.channel) === String(channel)) {
        console.log('[WEBRTC] call_accepted received from receiver');
        setAccepted(true);
        setConnected(true);
      }
    };

    const handleCallEnded = (data) => {
      console.log('[WEBRTC] Call ended signal received:', data);
      if (!data?.channel || String(data.channel) !== String(channel)) {
        console.log('[WEBRTC] Ignoring call ended for mismatch channel:', data?.channel);
        return;
      }
      endLocalCallSession();
      onClose();
    };

    const handleCallRejected = (data) => {
      console.log('[WEBRTC] Call rejected signal received:', data);
      if (accepted || connected) {
        console.log('[WEBRTC] Ignoring call_rejected signal because call is already accepted/connected');
        return;
      }
      if (!data?.channel || String(data.channel) !== String(channel)) {
        console.log('[WEBRTC] Ignoring call rejected for mismatch channel:', data?.channel);
        return;
      }
      endLocalCallSession();
      onClose();
    };

    socket.on('webrtc_offer', handleOffer);
    socket.on('webrtc_answer', handleAnswer);
    socket.on('webrtc_ice_candidate', handleCandidate);
    socket.on('call_accepted', handleCallAccepted);
    socket.on('call_ended', handleCallEnded);
    socket.on('call_rejected', handleCallRejected);

    return () => {
      socket.off('webrtc_offer', handleOffer);
      socket.off('webrtc_answer', handleAnswer);
      socket.off('webrtc_ice_candidate', handleCandidate);
      socket.off('call_accepted', handleCallAccepted);
      socket.off('call_ended', handleCallEnded);
      socket.off('call_rejected', handleCallRejected);
    };
  }, [channel, receiverId, pc, onClose, flushPendingCandidates, accepted, connected, endLocalCallSession]);

  // Clean end call action
  const leaveCall = () => {
    const socket = getSocket();
    if (socket?.connected && channel) {
      socket.emit('call_end', {
        otherId: receiverId,
        channel,
        status: 'completed',
        reason: 'ended_by_user'
      });
    }
    endLocalCallSession();
    onClose();
  };

  const toggleMic = () => {
    if (localStream) {
      localStream.getAudioTracks().forEach(t => t.enabled = !micOn);
      setMicOn(!micOn);
    }
  };

  const toggleCamera = () => {
    if (localStream && isVideo) {
      localStream.getVideoTracks().forEach(t => t.enabled = !cameraOn);
      setCameraOn(!cameraOn);
    }
  };

  const statusText = (accepted || connected)
    ? "Connected"
    : isCaller
      ? "Ringing..."
      : "Connecting...";

  const controlButtonClass = "flex h-14 w-14 items-center justify-center rounded-full text-white shadow-xl transition duration-200 hover:-translate-y-0.5";

  // Video Call Mode Layout
  if (isVideo) {
    return (
      <div className="relative h-[min(720px,92vh)] min-h-[600px] w-full overflow-hidden rounded-[28px] bg-black text-white shadow-2xl">
        <audio ref={setRemoteAudioRef} autoPlay playsInline style={{ display: 'none' }} />
        {/* Remote Video (Background) */}
        <div className="absolute inset-0 z-0 bg-gray-950 flex items-center justify-center">
          {accepted && connected ? (
            <video ref={setRemoteVideoRef} autoPlay playsInline className="h-full w-full object-cover" />
          ) : (
            <div className="flex flex-col items-center">
              <img src={receiverPic} alt={receiverName} className="h-32 w-32 rounded-full border-[4px] border-white/20 object-cover blur-sm opacity-50" />
            </div>
          )}
        </div>

        <div className="pointer-events-none absolute inset-0 z-[1] bg-gradient-to-b from-black/35 via-transparent to-black/65" />

        {/* Local Video Preview (PIP Overlay) */}
        <div className="absolute left-5 top-5 z-20 h-36 w-28 overflow-hidden rounded-2xl border border-white/30 bg-black/30 shadow-2xl">
          {localStream && cameraOn ? (
            <video ref={localVideoRef} autoPlay playsInline muted className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-gray-900">
              <FaVideoSlash className="text-xl text-white/50" />
            </div>
          )}
        </div>

        {/* Video Call Header */}
        <div className="pointer-events-none absolute left-1/2 top-5 z-20 -translate-x-1/2 rounded-full border border-white/20 bg-black/35 px-4 py-2 text-sm font-semibold backdrop-blur-md">
          <span className="inline-flex items-center gap-2">
            <FiVideo />
            Video call (WebRTC)
          </span>
        </div>

        {/* Call Info & Timer */}
        <div className="pointer-events-none absolute left-1/2 top-20 z-20 -translate-x-1/2 rounded-2xl bg-black/35 px-5 py-3 text-center shadow-xl backdrop-blur-md">
          <h2 className="m-0 max-w-[220px] truncate text-2xl font-bold">{receiverName}</h2>
          <p className="m-0 mt-1 text-sm font-semibold text-white/80">
            {accepted && connected ? formatDuration(elapsedSeconds) : statusText}
          </p>
        </div>

        {/* Control Buttons */}
        <div className="absolute bottom-6 left-1/2 z-30 flex w-[calc(100%-32px)] max-w-[390px] -translate-x-1/2 items-center justify-between rounded-[28px] bg-black/55 px-8 py-5 shadow-2xl backdrop-blur-xl">
          <button
            type="button"
            title={micOn ? "Mute microphone" : "Unmute microphone"}
            className={`${controlButtonClass} ${micOn ? "bg-black/70" : "bg-white/25"}`}
            onClick={toggleMic}
          >
            {micOn ? <FiMic className="text-2xl" /> : <FiMicOff className="text-2xl" />}
          </button>
          <button
            type="button"
            title="End call"
            className="flex h-16 w-16 items-center justify-center rounded-full bg-[#ff3f64] text-white shadow-[0_0_30px_rgba(255,63,100,0.45)] transition duration-200 hover:-translate-y-0.5 hover:bg-[#ff244f]"
            onClick={leaveCall}
          >
            <IoCall className="rotate-[135deg] text-3xl" />
          </button>
          <button
            type="button"
            title={cameraOn ? "Turn camera off" : "Turn camera on"}
            className={`${controlButtonClass} ${cameraOn ? "bg-black/70" : "bg-white/25"}`}
            onClick={toggleCamera}
          >
            {cameraOn ? <FaVideo className="text-xl" /> : <FaVideoSlash className="text-xl" />}
          </button>
        </div>
      </div>
    );
  }

  // Audio Call Mode Layout
  return (
    <div className="relative h-[min(660px,90vh)] min-h-[540px] w-full overflow-hidden rounded-[30px] bg-[#190028] text-white shadow-2xl">
      <audio ref={setRemoteAudioRef} autoPlay playsInline style={{ display: 'none' }} />
      <div
        className="absolute inset-0 scale-110 bg-cover bg-center opacity-25 blur-[16px]"
        style={{ backgroundImage: `url(${receiverPic})` }}
      />
      <div className="absolute inset-0 bg-[linear-gradient(160deg,rgba(171,34,255,0.95)_0%,rgba(117,21,208,0.96)_42%,rgba(37,6,58,0.98)_100%)]" />
      <div className="absolute left-1/2 top-24 h-56 w-56 -translate-x-1/2 rounded-full bg-white/12 blur-3xl" />
      <div className="absolute -left-16 bottom-20 h-56 w-56 rounded-full bg-[#ff4f8b]/20 blur-3xl" />

      <div className="relative z-[2] flex h-full flex-col items-center justify-between px-6 py-8">
        <div className="rounded-full bg-white/14 px-4 py-2 text-sm font-semibold text-white shadow-lg backdrop-blur-md">
          <span className="inline-flex items-center gap-2">
            <FiPhoneCall />
            Audio call (WebRTC)
          </span>
        </div>

        <div className="flex flex-col items-center text-center">
          <div className="relative mb-8">
            <div className="absolute inset-[-18px] animate-pulse rounded-full bg-white/18 blur-md" />
            <div className="absolute inset-[-34px] rounded-full border border-white/10" />
            <img
              src={receiverPic}
              alt={receiverName || "Caller"}
              className="relative h-36 w-36 rounded-full border-[5px] border-white object-cover shadow-2xl"
            />
          </div>
          <h2 className="m-0 max-w-[220px] truncate text-4xl font-bold leading-tight">{receiverName}</h2>
          <p className="m-0 mt-2 text-sm font-medium text-white/75">{statusText}</p>
          <p className="m-0 mt-4 rounded-full bg-black/22 px-5 py-2 text-sm font-semibold tracking-wide text-white/90 shadow-inner">
            {formatDuration(elapsedSeconds)}
          </p>
        </div>

        <div className="flex w-full max-w-[340px] items-center justify-between rounded-[30px] bg-black/42 px-9 py-5 shadow-[0_24px_60px_rgba(0,0,0,0.28)] backdrop-blur-xl">
          <button
            type="button"
            onClick={toggleMic}
            title={micOn ? "Mute microphone" : "Unmute microphone"}
            className={`group flex h-16 w-16 items-center justify-center rounded-full transition duration-200 hover:-translate-y-0.5 hover:shadow-xl ${micOn ? "bg-black/70 text-white" : "bg-white text-[#190028]"}`}
          >
            {micOn ? <FiMic className="text-2xl" /> : <FiMicOff className="text-2xl" />}
          </button>
          <button
            type="button"
            onClick={leaveCall}
            title="End call"
            className="flex h-16 w-16 items-center justify-center rounded-full bg-[#ff3f64] text-white shadow-[0_0_30px_rgba(255,63,100,0.45)] transition duration-200 hover:-translate-y-0.5 hover:bg-[#ff244f]"
          >
            <IoCall className="rotate-[135deg] text-3xl" />
          </button>
        </div>
      </div>
    </div>
  );
}
